import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Info, UploadCloud } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { createCampaigns } from '@/api/campaigns/create-campaigns'
import {
  getCampaignsModo,
  type GetCampaignsModoResponse,
} from '@/api/campaigns/get-campaigns-modo'
import {
  getCampaignsPrazo,
  type GetCampaignsPrazoResponse,
} from '@/api/campaigns/get-campaigns-prazo'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

const campanhaSchema = z.object({
  titulo: z
    .string()
    .min(3, 'Título deve ter no mínimo 3 caracteres.')
    .refine(
      (val) => {
        const trimmed = val.trim()
        return [...trimmed].length >= 3
      },
      {
        message:
          'Título deve ter no mínimo 3 caracteres visíveis (emojis contam como 1 caractere).',
      },
    ),
  descricao: z
    .string()
    .min(5, 'Descrição deve ter no mínimo 5 caracteres.')
    .refine(
      (val) => {
        const trimmed = val.trim()
        return [...trimmed].length >= 5
      },
      {
        message:
          'Descrição deve ter no mínimo 5 caracteres visíveis (emojis contam como 1 caractere).',
      },
    ),
  titulo_valor: z
    .string()
    .nonempty('Informe o valor do título.')
    .refine(
      (val) => {
        const num = parseFloat(val.replace(',', '.'))
        return !isNaN(num) && num > 0
      },
      {
        message: 'Informe um valor numérico válido maior que 0.',
      },
    ),
  titulo_quantidade: z
    .number()
    .min(1, 'Quantidade total deve ser maior que 0.'),
  titulo_min: z.number().min(1, 'Quantidade mínima deve ser maior que 0.'),
  titulo_max: z.number().min(1, 'Quantidade máxima deve ser maior que 0.'),
  dt_sorteio: z.string().optional(),
  modo_id: z.number({ invalid_type_error: 'Selecione um modo válido.' }),
  prazo_id: z.number({ invalid_type_error: 'Selecione um prazo válido.' }),
  notificar_grupo_whatsapp: z.boolean().optional(),
  nome_grupo_whatsapp: z
    .string()
    .optional()
    .refine(
      (val) => {
        return !val || val.trim().length >= 3
      },
      {
        message: 'Nome do grupo deve ter ao menos 3 caracteres se preenchido.',
      },
    ),
})

type CampanhaForm = z.infer<typeof campanhaSchema>

interface NewCampaingnsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewCampaingnsModal({
  open,
  onOpenChange,
}: NewCampaingnsModalProps) {
  const [step, setStep] = useState(1)
  const [fileName, setFileName] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState('')
  const [showPrazoTooltip, setShowPrazoTooltip] = useState(false)
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [showModoTooltip, setShowModoTooltip] = useState(false)
  const tooltipModoTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [showGrupoTooltip, setShowGrupoTooltip] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
    setValue,
    watch,
    trigger,
  } = useForm<CampanhaForm>({
    resolver: zodResolver(campanhaSchema),
  })

  const [prazoOptions, setPrazoOptions] = useState<GetCampaignsPrazoResponse[]>(
    [],
  )
  const [modoOptions, setModoOptions] = useState<GetCampaignsModoResponse[]>([])

  const { mutateAsync: createCampaignFn } = useMutation({
    mutationFn: createCampaigns,
  })

  const prazosMutation = useMutation({
    mutationFn: getCampaignsPrazo,
    onSuccess: setPrazoOptions,
  })

  const modosMutation = useMutation({
    mutationFn: getCampaignsModo,
    onSuccess: setModoOptions,
  })

  useEffect(() => {
    if (open) {
      prazosMutation.mutate()
      modosMutation.mutate()
    }
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }
  }, [open])

  // tootip prazo
  const handleShowTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }
    setShowPrazoTooltip(true)
  }

  const handleHideTooltip = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowPrazoTooltip(false)
    }, 300)
  }

  const handleTooltipClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowPrazoTooltip(!showPrazoTooltip)
  }

  // tootip modo

  const handleShowModoTooltip = () => {
    if (tooltipModoTimeoutRef.current) {
      clearTimeout(tooltipModoTimeoutRef.current)
    }
    setShowModoTooltip(true)
  }

  const handleHideModoTooltip = () => {
    tooltipModoTimeoutRef.current = setTimeout(() => {
      setShowModoTooltip(false)
    }, 300)
  }

  const handleModoTooltipClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowModoTooltip(!showModoTooltip)
  }

  async function onSubmit(data: CampanhaForm) {
    if (!selectedFile) {
      setFileError('Por favor, selecione uma imagem.')
      toast.error('Por favor, selecione uma imagem.')
      return
    }

    try {
      const payload = {
        ...data,
        titulo: data.titulo.normalize('NFC'),
        descricao: data.descricao.normalize('NFC'),
        titulo_valor: parseFloat(data.titulo_valor.replace(',', '.')),
        imagemFile: selectedFile,
        notificar_grupo_whatsapp: data.notificar_grupo_whatsapp ?? false,
        nome_grupo_whatsapp: data.nome_grupo_whatsapp ?? '',
        status: false,
      }

      await createCampaignFn(payload)

      toast.success('Campanha criada com sucesso!')
      reset()
      setSelectedFile(null)
      setFileName('')
      setFileError('')
      onOpenChange(false)
    } catch (err) {
      console.error('Erro ao criar campanha:', err)
      toast.error('Erro ao criar campanha. Verifique o console para detalhes.')
    } finally {
      window.location.reload()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setFileName(file.name)
      setFileError('')
    }
  }

  const handleNextStep = async () => {
    const isValid = await trigger(['titulo', 'descricao', 'dt_sorteio'])
    if (isValid) {
      setStep(2)
    }
  }

  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof CampanhaForm,
  ) => {
    let value = e.target.value
    value = value.replace(/\D/g, '')
    const numValue = value ? parseInt(value, 10) : 0
    const finalValue = Math.max(1, numValue)

    setValue(field, finalValue)
    e.target.value = finalValue.toString()
  }

  const handleInputFocus = (fieldId: string) => {
    const element = document.getElementById(fieldId)
    element?.classList.remove('border-red-500')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Criar nova campanha</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
          {step === 1 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="titulo">Título</Label>
                <Input
                  id="titulo"
                  {...register('titulo')}
                  className={errors.titulo ? 'border-red-500' : ''}
                  onFocus={() => handleInputFocus('titulo')}
                />
                {errors.titulo && (
                  <p className="text-sm text-red-500">
                    {errors.titulo.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  {...register('descricao')}
                  className={errors.descricao ? 'border-red-500' : ''}
                  onFocus={() => handleInputFocus('descricao')}
                  rows={4}
                />
                {errors.descricao && (
                  <p className="text-sm text-red-500">
                    {errors.descricao.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dt_sorteio">Data do Sorteio</Label>
                <div
                  className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm shadow-sm ${
                    errors.dt_sorteio ? 'border-red-500' : ''
                  }`}
                >
                  <input
                    id="dt_sorteio"
                    type="datetime-local"
                    className="w-full border-none bg-transparent outline-none"
                    {...register('dt_sorteio')}
                    onFocus={() => handleInputFocus('dt_sorteio')}
                  />
                </div>
              </div>

              <div
                className={`cursor-pointer rounded-xl border border-dashed p-6 text-center transition ${
                  fileError ? 'border-red-500' : ''
                }`}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <UploadCloud className="h-8 w-8" />
                  <p className="text-sm">
                    {fileName || 'Clique ou arraste uma imagem aqui'}
                  </p>
                </div>
              </div>
              {fileError && <p className="text-sm text-red-500">{fileError}</p>}

              <Button type="button" onClick={handleNextStep} className="w-full">
                Próxima Etapa
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="titulo_valor">Valor Unitário do Título</Label>
                  <Input
                    id="titulo_valor"
                    type="text"
                    inputMode="decimal"
                    {...register('titulo_valor')}
                    className={errors.titulo_valor ? 'border-red-500' : ''}
                    onFocus={() => handleInputFocus('titulo_valor')}
                    onChange={(e) => {
                      const raw = e.target.value
                      const formatted = raw.replace(/[^0-9,]/g, '')
                      setValue('titulo_valor', formatted)
                      e.target.value = formatted
                    }}
                  />
                  {errors.titulo_valor && (
                    <p className="text-sm text-red-500">
                      {errors.titulo_valor.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="titulo_quantidade">Quantidade Total</Label>
                  <Input
                    id="titulo_quantidade"
                    type="number"
                    min="1"
                    {...register('titulo_quantidade', { valueAsNumber: true })}
                    onChange={(e) => handleNumberInput(e, 'titulo_quantidade')}
                    className={errors.titulo_quantidade ? 'border-red-500' : ''}
                    onFocus={() => handleInputFocus('titulo_quantidade')}
                  />
                  {errors.titulo_quantidade && (
                    <p className="text-sm text-red-500">
                      {errors.titulo_quantidade.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="titulo_min">Quantidade Mínima</Label>
                  <Input
                    id="titulo_min"
                    type="number"
                    min="1"
                    {...register('titulo_min', { valueAsNumber: true })}
                    onChange={(e) => handleNumberInput(e, 'titulo_min')}
                    className={errors.titulo_min ? 'border-red-500' : ''}
                    onFocus={() => handleInputFocus('titulo_min')}
                  />
                  {errors.titulo_min && (
                    <p className="text-sm text-red-500">
                      {errors.titulo_min.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="titulo_max">Quantidade Máxima</Label>
                  <Input
                    id="titulo_max"
                    type="number"
                    min="1"
                    {...register('titulo_max', { valueAsNumber: true })}
                    onChange={(e) => handleNumberInput(e, 'titulo_max')}
                    className={errors.titulo_max ? 'border-red-500' : ''}
                    onFocus={() => handleInputFocus('titulo_max')}
                  />
                  {errors.titulo_max && (
                    <p className="text-sm text-red-500">
                      {errors.titulo_max.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Prazo para pagamento</Label>
                  <div
                    className="relative"
                    onMouseEnter={handleShowTooltip}
                    onMouseLeave={handleHideTooltip}
                    onClick={handleTooltipClick}
                  >
                    <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                    {showPrazoTooltip && (
                      <div
                        className="absolute left-full z-10 mt-2 ml-2 w-64 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg"
                        onMouseEnter={() => {
                          if (tooltipTimeoutRef.current) {
                            clearTimeout(tooltipTimeoutRef.current)
                          }
                        }}
                        onMouseLeave={handleHideTooltip}
                      >
                        <p className="font-medium text-gray-900">
                          Sobre o Prazo
                        </p>
                        <p className="mt-1 text-gray-700">
                          O prazo selecionado define o tempo que os
                          participantes terão para realizar o pagamento após a
                          compra da rifa. Escolha um período que dê tempo
                          suficiente para o pagamento ser confirmado.
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          Exemplo: &quot;7 dias&quot; significa que o
                          participante terá 1 semana para pagar.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <Select
                  onValueChange={(value) => setValue('prazo_id', Number(value))}
                  value={watch('prazo_id')?.toString() || ''}
                >
                  <SelectTrigger
                    className={`w-full ${errors.prazo_id ? 'border-red-500' : ''}`}
                  >
                    <SelectValue placeholder="Selecione um prazo" />
                  </SelectTrigger>
                  <SelectContent>
                    {prazoOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id.toString()}>
                        {option.descricao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.prazo_id && (
                  <p className="text-sm text-red-500">
                    {errors.prazo_id.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Modo</Label>
                  <div
                    className="relative"
                    onMouseEnter={handleShowModoTooltip}
                    onMouseLeave={handleHideModoTooltip}
                    onClick={handleModoTooltipClick}
                  >
                    <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                    {showModoTooltip && (
                      <div
                        className="absolute left-full z-10 mt-2 ml-2 w-64 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg"
                        onMouseEnter={() => {
                          if (tooltipModoTimeoutRef.current) {
                            clearTimeout(tooltipModoTimeoutRef.current)
                          }
                        }}
                        onMouseLeave={handleHideModoTooltip}
                      >
                        <p className="font-medium text-gray-900">
                          Sobre o Modo
                        </p>
                        <p className="mt-1 text-gray-700">
                          O modo define a dinâmica da campanha, com títulos
                          aleatórios, títulos expostos ou ambos.
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          Títulos Aleatórios: O sistema escolhe os números
                          automaticamente para o comprador. Títulos Expostos: O
                          comprador pode ver todos os números disponíveis e
                          escolher exatamente quais quer. Ambos: O comprador
                          pode escolher os números que quiser ou deixar o
                          sistema sortear automaticamente para ele.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <Select
                  onValueChange={(value) => setValue('modo_id', Number(value))}
                  value={watch('modo_id')?.toString() || ''}
                >
                  <SelectTrigger
                    className={`w-full ${errors.modo_id ? 'border-red-500' : ''}`}
                  >
                    <SelectValue placeholder="Selecione um modo" />
                  </SelectTrigger>
                  <SelectContent>
                    {modoOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id.toString()}>
                        {option.descricao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {errors.modo_id && (
                  <p className="text-sm text-red-500">
                    {errors.modo_id.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="notificar_grupo_whatsapp"
                    {...register('notificar_grupo_whatsapp')}
                  />
                  <Label htmlFor="notificar_grupo_whatsapp">
                    Notificar Grupo no WhatsApp
                  </Label>
                </div>
              </div>

              {watch('notificar_grupo_whatsapp') && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="nome_grupo_whatsapp">
                      Nome do Grupo no WhatsApp
                    </Label>

                    <div
                      className="relative"
                      onMouseEnter={() => setShowGrupoTooltip(true)}
                      onMouseLeave={() =>
                        setTimeout(() => setShowGrupoTooltip(false), 300)
                      }
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowGrupoTooltip((prev) => !prev)
                      }}
                    >
                      <Info className="h-4 w-4 cursor-pointer text-gray-500" />
                      {showGrupoTooltip && (
                        <div
                          className="absolute left-full z-10 mt-2 ml-2 w-72 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg"
                          onMouseEnter={() => setShowGrupoTooltip(true)}
                          onMouseLeave={() =>
                            setTimeout(() => setShowGrupoTooltip(false), 300)
                          }
                        >
                          <p className="font-medium text-gray-900">
                            Nome do Grupo
                          </p>
                          <p className="mt-1 text-gray-700">
                            O nome informado deve ser <b>exatamente igual</b> ao
                            nome do grupo no WhatsApp. Isso garante que a
                            notificação seja enviada corretamente.
                            <br />
                            <br />
                            Certifique-se também de adicionar o número{' '}
                            <strong>(19) 98992-4246</strong> como administrador
                            do grupo.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <Input
                    id="nome_grupo_whatsapp"
                    {...register('nome_grupo_whatsapp')}
                    className={
                      errors.nome_grupo_whatsapp ? 'border-red-500' : ''
                    }
                  />
                  {errors.nome_grupo_whatsapp && (
                    <p className="text-sm text-red-500">
                      {errors.nome_grupo_whatsapp.message}
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-col justify-between gap-4 md:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="md:w-1/3"
                  onClick={() => setStep(1)}
                >
                  Voltar
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="md:w-2/3"
                >
                  Criar Campanha
                </Button>
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}
